import { useCallback, useEffect, useState } from "react";

/* ==========================================================
   CONFIG  (addResort.jsx wali same API_BASE rakho)
========================================================== */
const API_BASE = "https://bmtvendorvendorfinal.onrender.com";
const RESORT_API = `${API_BASE}/api/resorts`;
const ROOM_API = `${API_BASE}/api/resort-rooms`;
const UPLOAD_API = `${API_BASE}/api/upload`;
const UPLOAD_FIELD = "file"; // Upload.routes: .single("file")

const BED_TYPES = ["King", "Queen", "Double", "Twin", "Single", "Bunk", "Sofa bed"];

const MEAL_PLANS = [
  ["ROOM_ONLY", "Room only", "No meals included"],
  ["BREAKFAST", "Breakfast", "Breakfast included"],
  ["HALF_BOARD", "Half board", "Breakfast and dinner"],
  ["FULL_BOARD", "Full board", "Breakfast, lunch and dinner"],
];

const ROOM_AMENITIES = [
  "Air Conditioning", "Balcony", "Private Pool", "Sea View", "Mountain View", "Garden View",
  "Bathtub", "Mini Bar", "TV", "Free Wi-Fi", "Tea / Coffee Maker", "Safe", "Work Desk", "Heater",
];

const EMPTY_FORM = {
  name: "",
  description: "",
  roomSize: "",
  roomSizeUnit: "sqft",
  bedType: "",
  numberOfBeds: 1,
  maxAdults: 2,
  maxChildren: 0,
  maxGuests: 2,
  basePrice: "",
  extraAdultPrice: 0,
  extraChildPrice: 0,
  mealPlans: [],
  amenities: [],
  images: [],
  isActive: true,
};

/* ==========================================================
   HELPERS
========================================================== */
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("vendorToken") || ""}` });

async function api(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });
  let data = {};
  try { data = await res.json(); } catch { /* empty body */ }
  if (res.status === 401 && data.code === "TOKEN_EXPIRED") {
    localStorage.removeItem("vendorToken");
    localStorage.removeItem("vendorUser");
    window.location.href = "/vendor-login";
  }
  if (!res.ok || data.success === false) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
}

function extractUploaded(data) {
  const raw = data.file || data.files || data.images || data.urls || data.data || data.result || data.url || data.secure_url || data;
  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .map((f) =>
      typeof f === "string"
        ? { url: f, publicId: "" }
        : { url: f?.url || f?.secure_url || f?.fileUrl || f?.path || "", publicId: f?.publicId || f?.public_id || "" }
    )
    .filter((f) => f.url);
}

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const num = (v, fallback = 0) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? fallback : Number(v));

function validate(f) {
  if (!f.name.trim()) return "Room name is required.";
  if (f.basePrice === "" || Number.isNaN(Number(f.basePrice)) || Number(f.basePrice) < 0) return "Enter a valid base price.";
  if (num(f.maxAdults) < 1) return "At least 1 adult must be allowed.";
  if (num(f.numberOfBeds) < 1) return "Number of beds must be at least 1.";
  if (num(f.maxGuests) < 1) return "Max guests must be at least 1.";
  if (num(f.maxGuests) > num(f.maxAdults) + num(f.maxChildren)) return "Max guests cannot be more than max adults + max children.";
  return "";
}

/* ==========================================================
   SMALL UI PIECES
========================================================== */
function Input({ label, value, onChange, type = "text", placeholder, required, hint, ...rest }) {
  return (
    <div className="bmt-field-group">
      <label>{label}{required && " *"}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} {...rest} />
      {hint && <small style={{ color: "#6b7280" }}>{hint}</small>}
    </div>
  );
}

function Notice({ kind, children }) {
  if (!children) return null;
  const bad = kind === "error";
  return (
    <div
      role={bad ? "alert" : "status"}
      style={{
        padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 14,
        background: bad ? "#fef2f2" : "#f0fdf4", color: bad ? "#991b1b" : "#166534",
        border: `1px solid ${bad ? "#fecaca" : "#bbf7d0"}`,
      }}
    >
      {children}
    </div>
  );
}

/* ==========================================================
   MAIN COMPONENT
========================================================== */
export default function ResortRooms() {
  const [resorts, setResorts] = useState([]);
  const [resortId, setResortId] = useState(() => sessionStorage.getItem("bmtResortRoomsResort") || "");
  const [loadingResorts, setLoadingResorts] = useState(true);

  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  const [view, setView] = useState("list"); // list | form
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [customAmenity, setCustomAmenity] = useState("");

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const setTop = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  // Adults / children badle to max guests automatically sync (baad me edit kar sakte ho)
  const setCapacity = (key, val) =>
    setForm((p) => {
      const next = { ...p, [key]: val };
      if (key !== "maxGuests") next.maxGuests = num(next.maxAdults) + num(next.maxChildren);
      return next;
    });

  /* ---------- LOAD RESORTS ---------- */
  useEffect(() => {
    (async () => {
      try {
        const data = await api(`${RESORT_API}/my-resorts`);
        const list = Array.isArray(data.resorts) ? data.resorts : [];
        setResorts(list);
        setResortId((cur) => (list.some((r) => r._id === cur) ? cur : list[0]?._id || ""));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingResorts(false);
      }
    })();
  }, []);

  /* ---------- LOAD ROOMS ---------- */
  const loadRooms = useCallback(async () => {
    if (!resortId) { setRooms([]); return; }
    setLoadingRooms(true);
    try {
      const data = await api(`${ROOM_API}/resort/${resortId}`);
      setRooms(Array.isArray(data.rooms) ? data.rooms : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingRooms(false);
    }
  }, [resortId]);

  useEffect(() => {
    if (resortId) sessionStorage.setItem("bmtResortRoomsResort", resortId);
    loadRooms();
  }, [resortId, loadRooms]);

  const resortName = resorts.find((r) => r._id === resortId)?.name || "";

  /* ---------- OPEN FORM ---------- */
  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError("");
    setSuccess("");
    setView("form");
  };

  const startEdit = async (id) => {
    setError("");
    setSuccess("");
    try {
      const data = await api(`${ROOM_API}/${id}`);
      const r = data.room;
      setForm({
        ...EMPTY_FORM,
        ...r,
        roomSize: r.roomSize || "",
        mealPlans: r.mealPlans || [],
        amenities: r.amenities || [],
        images: r.images || [],
      });
      setEditingId(id);
      setView("form");
    } catch (e) {
      setError(e.message);
    }
  };

  /* ---------- MEAL PLANS ---------- */
  const hasPlan = (name) => form.mealPlans.some((m) => m.name === name);
  const planPrice = (name) => form.mealPlans.find((m) => m.name === name)?.price ?? 0;

  const togglePlan = (name) =>
    setTop("mealPlans", hasPlan(name) ? form.mealPlans.filter((m) => m.name !== name) : [...form.mealPlans, { name, price: 0 }]);

  const setPlanPrice = (name, price) =>
    setTop("mealPlans", form.mealPlans.map((m) => (m.name === name ? { ...m, price } : m)));

  /* ---------- AMENITIES ---------- */
  const toggleAmenity = (name) =>
    setTop("amenities", form.amenities.includes(name) ? form.amenities.filter((a) => a !== name) : [...form.amenities, name]);

  const addCustomAmenity = () => {
    const v = customAmenity.trim();
    if (!v || form.amenities.some((a) => a.toLowerCase() === v.toLowerCase())) return setCustomAmenity("");
    setTop("amenities", [...form.amenities, v]);
    setCustomAmenity("");
  };

  /* ---------- IMAGES (ek-ek file upload) ---------- */
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (form.images.length + files.length > 20) return setError("You can add up to 20 images per room.");
    if (files.some((f) => !f.type.startsWith("image/"))) return setError("Only image files are allowed here.");
    if (files.some((f) => f.size > 10 * 1024 * 1024)) return setError("Each image must be under 10 MB.");

    setUploading(true);
    setError("");

    const uploaded = [];
    let failed = 0;
    let lastError = "";

    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append(UPLOAD_FIELD, file);
        const data = await api(UPLOAD_API, { method: "POST", body: fd });
        const items = extractUploaded(data);
        if (!items.length) throw new Error("Upload worked but no image URL came back from the server.");
        uploaded.push(items[0]);
      } catch (err) {
        failed += 1;
        lastError = err.message;
      }
    }

    if (uploaded.length) setForm((p) => ({ ...p, images: [...p.images, ...uploaded] }));
    if (failed) setError(`${failed} of ${files.length} photos failed to upload: ${lastError}`);
    setUploading(false);
  };

  const removeImage = (i) => setForm((p) => ({ ...p, images: p.images.filter((_, x) => x !== i) }));

  /* ---------- SAVE ---------- */
  const save = async () => {
    const problem = validate(form);
    if (problem) { setError(problem); setSuccess(""); return; }

    setSaving(true);
    setError("");
    setSuccess("");

    const payload = {
      name: form.name.trim(),
      description: form.description,
      roomSize: num(form.roomSize),
      roomSizeUnit: form.roomSizeUnit,
      bedType: form.bedType,
      numberOfBeds: num(form.numberOfBeds, 1),
      maxAdults: num(form.maxAdults, 2),
      maxChildren: num(form.maxChildren),
      maxGuests: num(form.maxGuests, 2),
      basePrice: num(form.basePrice),
      extraAdultPrice: num(form.extraAdultPrice),
      extraChildPrice: num(form.extraChildPrice),
      mealPlans: form.mealPlans.map((m) => ({ name: m.name, price: num(m.price) })),
      amenities: form.amenities,
      images: form.images,
      isActive: form.isActive,
    };

    try {
      if (editingId) {
        await api(`${ROOM_API}/${editingId}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        await api(ROOM_API, { method: "POST", body: JSON.stringify({ ...payload, resortId }) });
      }
      await loadRooms();
      setView("list");
      setSuccess(editingId ? "Room category updated." : "Room category added.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (room) => {
    if (!window.confirm(`Delete "${room.name}"? This cannot be undone.`)) return;
    setError("");
    try {
      await api(`${ROOM_API}/${room._id}`, { method: "DELETE" });
      setSuccess("Room category deleted.");
      loadRooms();
    } catch (e) {
      setError(e.message);
    }
  };

  /* ==========================================================
     NO RESORT YET
  ========================================================== */
  if (!loadingResorts && resorts.length === 0) {
    return (
      <div className="bmt-premium-page">
        <div className="bmt-section-intro">
          <div><h2>Rooms & Categories</h2><p>Create room categories with prices and capacity for your resort.</p></div>
        </div>
        <Notice kind="error">{error}</Notice>
        <section className="bmt-form-card">
          <div className="bmt-empty-state">
            <h3>Add a resort first</h3>
            <p>Room categories belong to a resort. Open My Resorts from the menu and add your resort, then come back here.</p>
          </div>
        </section>
      </div>
    );
  }

  /* ==========================================================
     LIST VIEW
  ========================================================== */
  if (view === "list") {
    return (
      <div className="bmt-premium-page">
        <div className="bmt-section-intro">
          <div>
            <h2>Rooms & Categories</h2>
            <p>Set up the room types guests can book, with capacity, price and meal plans.</p>
          </div>
          <button className="bmt-primary-btn" onClick={startNew} disabled={!resortId}>+ Add Room Category</button>
        </div>

        <Notice kind="error">{error}</Notice>
        <Notice kind="success">{success}</Notice>

        <section className="bmt-form-card">
          <div className="bmt-field-group" style={{ maxWidth: 360 }}>
            <label>Resort</label>
            <select
              value={resortId}
              onChange={(e) => { setResortId(e.target.value); setSuccess(""); setError(""); }}
              disabled={loadingResorts}
            >
              {resorts.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </div>
        </section>

        <section className="bmt-form-card">
          {loadingRooms ? (
            <p>Loading room categories...</p>
          ) : rooms.length === 0 ? (
            <div className="bmt-empty-state">
              <h3>No room categories in {resortName || "this resort"}</h3>
              <p>Add your first room category, for example Deluxe Room or Pool Villa.</p>
              <button className="bmt-primary-btn" onClick={startNew}>Add room category</button>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 14 }}>
              {rooms.map((room) => (
                <article
                  key={room._id}
                  style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", padding: 14, border: "1px solid #e5e7eb", borderRadius: 10 }}
                >
                  <div style={{ width: 120, height: 80, borderRadius: 8, overflow: "hidden", background: "#f3f4f6", flexShrink: 0 }}>
                    {room.images?.[0]?.url && (
                      <img src={room.images[0].url} alt={room.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <strong style={{ fontSize: 16 }}>{room.name}</strong>
                    <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                      {[room.bedType && `${room.numberOfBeds} ${room.bedType} bed${room.numberOfBeds > 1 ? "s" : ""}`,
                        `Up to ${room.maxGuests} guests`,
                        room.roomSize ? `${room.roomSize} ${room.roomSizeUnit}` : ""]
                        .filter(Boolean).join(" • ")}
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      <b>{money(room.basePrice)}</b> <span style={{ color: "#6b7280" }}>base price per night</span>
                    </div>
                    <span style={{ display: "inline-block", marginTop: 6, fontSize: 12, fontWeight: 600, color: room.isActive ? "#15803d" : "#6b7280" }}>
                      {room.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="bmt-secondary-btn" onClick={() => startEdit(room._id)}>Edit</button>
                    <button className="bmt-secondary-btn" onClick={() => remove(room)}>Delete</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    );
  }

  /* ==========================================================
     FORM VIEW
  ========================================================== */
  return (
    <div className="bmt-premium-page">
      <div className="bmt-section-intro">
        <div>
          <h2>{editingId ? "Edit Room Category" : "Add Room Category"}</h2>
          <p>Resort: <b>{resortName}</b></p>
        </div>
        <button className="bmt-secondary-btn" onClick={() => { setView("list"); setError(""); }}>Back to list</button>
      </div>

      <Notice kind="error">{error}</Notice>

      {/* BASIC */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Room details</h3></div>
        <div className="bmt-form-grid">
          <Input label="Room name" required value={form.name} onChange={(v) => setTop("name", v)} placeholder="e.g. Deluxe Room" />
          <div className="bmt-field-group">
            <label>Bed type</label>
            <select value={form.bedType} onChange={(e) => setTop("bedType", e.target.value)}>
              <option value="">Select</option>
              {BED_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <Input label="Number of beds" type="number" min="1" value={form.numberOfBeds} onChange={(v) => setTop("numberOfBeds", v)} />
          <Input label="Room size" type="number" min="0" value={form.roomSize} onChange={(v) => setTop("roomSize", v)} placeholder="e.g. 350" />
          <div className="bmt-field-group">
            <label>Size unit</label>
            <select value={form.roomSizeUnit} onChange={(e) => setTop("roomSizeUnit", e.target.value)}>
              <option value="sqft">Square feet</option>
              <option value="sqm">Square metres</option>
            </select>
          </div>
        </div>
        <div className="bmt-field-group">
          <label>Description</label>
          <textarea rows={4} value={form.description} onChange={(e) => setTop("description", e.target.value)} placeholder="Describe the room, view and what makes it special" />
        </div>
      </section>

      {/* CAPACITY */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Guest capacity</h3></div>
        <div className="bmt-form-grid">
          <Input label="Max adults" type="number" min="1" value={form.maxAdults} onChange={(v) => setCapacity("maxAdults", v)} />
          <Input label="Max children" type="number" min="0" value={form.maxChildren} onChange={(v) => setCapacity("maxChildren", v)} />
          <Input label="Max guests in total" type="number" min="1" value={form.maxGuests} onChange={(v) => setCapacity("maxGuests", v)} hint="Cannot be more than adults + children" />
        </div>
      </section>

      {/* PRICING */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head">
          <h3>Pricing</h3>
          <p>Base price is the default nightly rate. Date-wise rates can be set later.</p>
        </div>
        <div className="bmt-form-grid">
          <Input label="Base price per night (₹)" required type="number" min="0" value={form.basePrice} onChange={(v) => setTop("basePrice", v)} placeholder="e.g. 4500" />
          <Input label="Extra adult per night (₹)" type="number" min="0" value={form.extraAdultPrice} onChange={(v) => setTop("extraAdultPrice", v)} />
          <Input label="Extra child per night (₹)" type="number" min="0" value={form.extraChildPrice} onChange={(v) => setTop("extraChildPrice", v)} />
        </div>
      </section>

      {/* MEAL PLANS */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head">
          <h3>Meal plans</h3>
          <p>Choose the plans guests can pick. The price is the extra amount added to the base price.</p>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {MEAL_PLANS.map(([key, label, desc]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <label className={`bmt-check-card ${hasPlan(key) ? "selected" : ""}`} style={{ minWidth: 260, flex: "1 1 260px" }}>
                <input type="checkbox" checked={hasPlan(key)} onChange={() => togglePlan(key)} />
                <span>{label} <small style={{ color: "#6b7280" }}>({desc})</small></span>
              </label>
              {hasPlan(key) && (
                <div className="bmt-field-group" style={{ margin: 0, width: 190 }}>
                  <input
                    type="number" min="0" aria-label={`${label} extra price`}
                    value={planPrice(key)} onChange={(e) => setPlanPrice(key, e.target.value)}
                    placeholder="Extra ₹ per night"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* AMENITIES */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Room amenities</h3><p>{form.amenities.length} selected</p></div>
        <div className="bmt-check-grid">
          {ROOM_AMENITIES.map((item) => (
            <label className={`bmt-check-card ${form.amenities.includes(item) ? "selected" : ""}`} key={item}>
              <input type="checkbox" checked={form.amenities.includes(item)} onChange={() => toggleAmenity(item)} />
              <span>{item}</span>
            </label>
          ))}
        </div>
        <div className="bmt-field-group">
          <label>Add another amenity</label>
          <div className="bmt-inline-input">
            <input
              value={customAmenity}
              placeholder="e.g. Private plunge pool"
              onChange={(e) => setCustomAmenity(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomAmenity(); } }}
            />
            <button type="button" onClick={addCustomAmenity}>Add</button>
          </div>
        </div>
        <div className="bmt-chip-wrap">
          {form.amenities.filter((a) => !ROOM_AMENITIES.includes(a)).map((a) => (
            <span className="bmt-chip" key={a}>
              {a}
              <button type="button" aria-label={`Remove ${a}`} onClick={() => toggleAmenity(a)}>×</button>
            </span>
          ))}
        </div>
      </section>

      {/* IMAGES */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Room photos</h3><p>The first photo is shown on the room card.</p></div>
        <label className="bmt-upload-zone">
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleUpload} disabled={uploading} />
          <span className="bmt-upload-icon">＋</span>
          <strong>{uploading ? "Uploading..." : "Upload room photos"}</strong>
          <small>JPG, PNG or WEBP, up to 10 MB each</small>
        </label>
        {form.images.length > 0 && (
          <div className="bmt-media-grid">
            {form.images.map((img, i) => (
              <div className="bmt-media-item" key={`${img.url}-${i}`}>
                <img src={img.url} alt={`Room photo ${i + 1}`} />
                <button type="button" onClick={() => removeImage(i)}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* VISIBILITY */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Availability</h3></div>
        <label className={`bmt-check-card ${form.isActive ? "selected" : ""}`} style={{ maxWidth: 360 }}>
          <input type="checkbox" checked={form.isActive} onChange={(e) => setTop("isActive", e.target.checked)} />
          <span>Guests can book this room category</span>
        </label>
      </section>

      <div className="bmt-form-actions">
        <button className="bmt-secondary-btn" disabled={saving} onClick={() => { setView("list"); setError(""); }}>Cancel</button>
        <button className="bmt-primary-btn" disabled={saving || uploading} onClick={save}>
          {saving ? "Saving..." : editingId ? "Save changes" : "Add room category"}
        </button>
      </div>
    </div>
  );
}